import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';

import {
  PlusOutlined,
  BulbOutlined
} from "@ant-design/icons";

import commonComponents from "../../../components/common";
import itemsComponents from "../../../components/items";

import Filter from "./components/secrets/Filter";
import TableData from "./components/secrets/TableData";
import BoxData from "./components/secrets/BoxData";
import FormData from "./components/secrets/FormData";
import InstallationGuideModal from "./components/InstallationGuideModal";

import { CipherType } from "../../../core-js/src/enums";

import {
  getRouterByLocation,
  scrollToTop,
  scrollEnd
} from '../../../utils/common';

import global from "../../../config/global";
import coreServices from "../../../services/core";
import secretServices from "../../../services/secret";
import environmentServices from "../../../services/environment";
import commonServices from "../../../services/common";

import storeActions from "../../../store/actions";

import { socketSyncSecrets } from "../../../utils/socket";

const ProjectSecrets = () => {
  const { PageHeader } = commonComponents;
  const { NoData, Pagination, MultipleSelect } = itemsComponents;
  const { t } = useTranslation();
  const location = useLocation();

  const currentPage = getRouterByLocation(location);
  const projectId = currentPage?.params?.project_id;
  const workspaceId = currentPage?.params?.workspace_id;

  const isMobile = useSelector((state) => state.system.isMobile)
  const isReadOnly = useSelector((state) => state.project.isReadOnly)
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const socketLastMessage = useSelector((state) => state.system.socketLastMessage);
  const installTool = useSelector((state) => state.onboarding.installTool);
  const createSecret = useSelector((state) => state.onboarding.createSecret);
  const projectSecrets = useSelector((state) => state.project.projectSecrets);
  const isFetchProjectSecrets = useSelector((state) => state.project.isFetchProjectSecrets);

  const syncAttemptedRef = useRef(false);
  const fetchRequestIdRef = useRef(0);
  const hasFetchedWithHashRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [callingAPI, setCallingAPI] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [installationVisible, setInstallationVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [total, setTotal] = useState(0);
  const [secrets, setSecrets] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [params, setParams] = useState({
    page: 1,
    size: global.constants.PAGE_SIZE,
    key: '',
    environmentId: null,
    orderField: 'updatedDate',
    orderDirection: 'desc'
  });

  const isProjectMatched = selectedProject && selectedProject.id == projectId;

  const parseDateToMs = (val) => {
    if (!val) return 0;
    if (val instanceof Date) return val.getTime() || 0;
    const num = Number(val);
    if (!isNaN(num) && num > 0) {
      return num > 10000000000 ? num : num * 1000;
    }
    const parsed = new Date(val).getTime();
    return isNaN(parsed) ? 0 : parsed;
  };

  const filteredAndSortedSecrets = useMemo(() => {
    if (!isProjectMatched) return [];
    let list = [...projectSecrets];

    if (params.key) {
      const searchKey = params.key.toLowerCase();
      list = list.filter((s) => s.key?.toLowerCase().includes(searchKey));
    }

    if (params.environmentId) {
      list = list.filter((s) => s.environmentId === params.environmentId);
    }

    const orderField = params.orderField || 'updatedDate';
    const orderDirection = params.orderDirection || 'desc';

    list.sort((a, b) => {
      if (orderField === 'name') {
        const valA = (a.key || a.name || '').toLowerCase();
        const valB = (b.key || b.name || '').toLowerCase();
        const comp = valA.localeCompare(valB);
        return orderDirection === 'desc' ? -comp : comp;
      }
      if (orderField === 'updatedDate' || orderField === 'revisionDate') {
        const timeA = parseDateToMs(a.updatedDate || a.updated_date || a.creationDate || a.creation_date);
        const timeB = parseDateToMs(b.updatedDate || b.updated_date || b.creationDate || b.creation_date);
        if (timeA !== timeB) {
          return orderDirection === 'desc' ? timeB - timeA : timeA - timeB;
        }
        const keyA = (a.key || a.name || '').toLowerCase();
        const keyB = (b.key || b.name || '').toLowerCase();
        return keyA.localeCompare(keyB);
      }
      return 0;
    });

    return list;
  }, [isProjectMatched, projectSecrets, params.key, params.environmentId, params.orderField, params.orderDirection]);

  const currentPagingSecrets = useMemo(() => {
    if (projectSecrets.length > 0) {
      const startIndex = (params.page - 1) * params.size;
      return filteredAndSortedSecrets.slice(startIndex, startIndex + params.size);
    }
    return [];
  }, [filteredAndSortedSecrets, params.page, params.size, projectSecrets.length]);

  const filterHash = useMemo(() => {
    if (projectSecrets.length > 0) {
      const hashes = currentPagingSecrets.map((s) => s.hash).filter(Boolean);
      return hashes.length > 0 ? hashes.join(',') : null;
    }
    return null;
  }, [currentPagingSecrets, projectSecrets.length]);

  useEffect(() => {
    if (selectedProject && selectedProject?.role !== global.constants.PROJECT_MEMBER_ROLE.READ_ONLY && installTool) {
      setInstallationVisible(true)
    }
  }, [installTool])

  useEffect(() => {
    if (createSecret) {
      handleOpenForm()
    }
  }, [createSecret])

  useEffect(() => {
    if (selectedProject && !selectedProject?.project_onboarding && selectedProject?.role !== global.constants.PROJECT_MEMBER_ROLE.READ_ONLY) {
      setInstallationVisible(true)
    } else {
      setInstallationVisible(false)
    }
  }, [selectedProject?.project_onboarding])

  useEffect(() => {
    hasFetchedWithHashRef.current = false;
    syncAttemptedRef.current = false;
    setSecrets([]);
    setTotal(0);
    fetchEnvironments();
  }, [projectId]);

  useEffect(() => {
    if (
      isProjectMatched &&
      selectedProject.total_secret > 0 &&
      projectSecrets.length === 0 &&
      !isFetchProjectSecrets &&
      !syncAttemptedRef.current
    ) {
      syncAttemptedRef.current = true;
      commonServices.sync_project_secrets(selectedProject);
    }
  }, [isProjectMatched, selectedProject?.total_secret, projectSecrets.length, isFetchProjectSecrets]);

  useEffect(() => {
    fetchData();
  }, [projectId, JSON.stringify(params)]);

  useEffect(() => {
    if (isProjectMatched && !isFetchProjectSecrets && projectSecrets.length > 0) {
      fetchData();
    }
  }, [isProjectMatched, isFetchProjectSecrets, projectSecrets]);

  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      key: currentPage?.query?.key,
    })
  }, [currentPage?.query?.key])

  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      size: global.constants.PAGE_SIZE
    })
  }, [isMobile])

  useEffect(() => {
    if (socketLastMessage) {
      try {
        const strData = socketLastMessage?.data ? socketLastMessage.data.split("'").join('"') : null;
        if (!strData) return;
        const messageData = JSON.parse(strData);
        if (messageData.event === 'sync') {
          if (messageData.data?.project_id == projectId && messageData?.data?.id && messageData.type?.includes('secret')) {
            socketSyncSecrets(messageData);
          }
        }
      } catch (e) {
        console.error("Socket message parse error:", e);
      }
    }
  }, [socketLastMessage, projectId]);

  const handleChangePage = (page, size) => {
    scrollToTop();
    setParams({
      ...params,
      page,
      size
    })
  };

  const fetchData = async () => {
    if (isProjectMatched && selectedProject?.total_secret === 0) {
      setTotal(0);
      setSecrets([]);
      setLoading(false);
      return;
    }

    if (projectSecrets.length > 0 && filteredAndSortedSecrets.length === 0) {
      setTotal(0);
      setSecrets([]);
      setLoading(false);
      return;
    }

    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);

    try {
      let keyHash = filterHash;
      if (params.key && !keyHash && projectSecrets.length === 0) {
        keyHash = await coreServices.hash_key_with_project_id(params.key, projectId);
      }
      const queryParams = {
        page: keyHash ? 1 : params.page,
        size: params.size,
        hash: keyHash,
        environment_id: params.environmentId
      };

      const response = await secretServices.list(projectId, queryParams);
      if (requestId !== fetchRequestIdRef.current) return;

      const decryptedSecrets = await coreServices.decrypt_ciphers(response.results, CipherType.Secret);
      if (requestId !== fetchRequestIdRef.current) return;

      await updateHashCiphers(response.results, decryptedSecrets);
      if (requestId !== fetchRequestIdRef.current) return;

      let finalSecrets = decryptedSecrets;
      if (currentPagingSecrets.length > 0) {
        finalSecrets = currentPagingSecrets
          .map((item) => {
            const found = decryptedSecrets.find((s) => s.id == item.id);
            if (!found) return null;
            return {
              ...found,
              creationDate: item.creationDate || found.creationDate,
              updatedDate: item.updatedDate || found.updatedDate,
              revisionDate: item.revisionDate || found.revisionDate,
            };
          })
          .filter(Boolean);
      } else if (finalSecrets.length > 0) {
        finalSecrets.sort((a, b) => {
          if (params.orderField === 'name') {
            const valA = (a.secret?.key || a.key || a.name || '').toLowerCase();
            const valB = (b.secret?.key || b.key || b.name || '').toLowerCase();
            const comp = valA.localeCompare(valB);
            return params.orderDirection === 'desc' ? -comp : comp;
          }
          const timeA = parseDateToMs(a.updatedDate || a.updated_date || a.creationDate || a.creation_date);
          const timeB = parseDateToMs(b.updatedDate || b.updated_date || b.creationDate || b.creation_date);
          if (timeA !== timeB) {
            return params.orderDirection === 'desc' ? timeB - timeA : timeA - timeB;
          }
          const keyA = (a.secret?.key || a.key || a.name || '').toLowerCase();
          const keyB = (b.secret?.key || b.key || b.name || '').toLowerCase();
          return keyA.localeCompare(keyB);
        });
      }

      if (projectSecrets.length > 0) {
        setTotal(filteredAndSortedSecrets.length);
      } else {
        setTotal(response.count);
      }
      setSecrets(finalSecrets);
    } catch (err) {
      if (requestId === fetchRequestIdRef.current) {
        setTotal(0);
        setSecrets([]);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchEnvironments = async () => {
    await environmentServices.list(projectId, { paging: 0 }).then(async (response) => {
      const environments = await coreServices.decrypt_ciphers(response.results, CipherType.Environment)
      setEnvironments(environments);
    }).catch(() => {
      setEnvironments([]);
    })
  }

  const handleOpenForm = (item = null) => {
    setSelectedItem(item);
    setFormVisible(true)
  }

  const deleteItem = (secret) => {
    global.confirm(() => {
      secretServices.remove(projectId, secret.id).then(async () => {
        global.pushSuccess(t('notification.success.secret.deleted'));
        global.store.dispatch(storeActions.updateIsFetchProjects(true));
        if (secrets.length === 1 && params.page > 1) {
          setParams({
            ...params,
            page: params.page - 1
          })
        } else {
          fetchData();
        }
      }).catch((error) => {
        global.pushError(error)
      });
    });
  };

  const updateHashCiphers = async (items, ciphers) => {
    const noHashItems = items.filter((i) => !i.secret_hash || i.secret_hash.includes('=')).map((i) => ciphers.find((c) => c.id === i.id));
    if (noHashItems.length > 0) {
      const hashPayload = await secretServices.hash_payload(noHashItems);
      await secretServices.update_multiple(projectId, hashPayload);
    }
  }

  const deleteItems = () => {
    global.confirm(async () => {
      setCallingAPI(true)
      await secretServices.remove_multiple(projectId, selectedRowKeys).then(async () => {
        global.pushSuccess(t('notification.success.secret.deleted'));
        if (secrets.length === selectedRowKeys.length && params.page > 1) {
          setParams({
            ...params,
            page: params.page - 1
          })
        } else {
          fetchData()
        }
        setSelectedRowKeys([]);
      }).catch((error) => {
        global.pushError(error)
      });
      setCallingAPI(false)
    });
  };

  const handleSelectionChange = (keys, key, value) => {
    if (keys) {
      const selectedSecrets = secrets.filter((secret) => keys.includes(secret.id))
      setSelectedRowKeys(selectedSecrets.map((secret) => secret.id))
    } else {
      const selectedKeys = value ? [...selectedRowKeys, key] : selectedRowKeys.filter((k) => k !== key);
      setSelectedRowKeys(selectedKeys)
    }
  }

  return (
    <div
      className="secrets layout-content"
      onScroll={(e) => scrollEnd(e, params, total, setParams)}
    >
      <PageHeader
        title={t('secrets.title')}
        total={total}
        subtitle={t('secrets.description')}
        docLabel={t('secrets.developer_tool_installation_guide')}
        docOnboarding={t('onboarding.developer_tool_installation_guide')}
        docClick={() => setInstallationVisible(true)}
        actions={[
          {
            key: 'start_to_use',
            label: t('secrets.start_to_use'),
            type: 'default',
            icon: <BulbOutlined />,
            hide: isReadOnly,
            tooltipTitle: <div>
              <p className="font-semibold">{t('secrets.tooltip.title')}</p>
              <ol className="ml-4">
                <li>{t('secrets.tooltip.step1')}</li>
                <li>{t('secrets.tooltip.step2')}</li>
              </ol>
            </div>,
            click: () => { global.navigate('PROJECT_ACCESS_KEY', { workspace_id: workspaceId, project_id: projectId }) }
          },
          {
            key: 'add',
            label: t('secrets.new'),
            type: 'primary',
            icon: <PlusOutlined />,
            hide: isReadOnly,
            click: () => handleOpenForm()
          },
        ]}
      />
      {
        selectedProject?.total_secret > 0 && <div className="min-h-[55px] mt-6">
          {
            selectedRowKeys.length > 0 ? <MultipleSelect
              className={'pt-[13px]'}
              selectedRowKeys={selectedRowKeys}
              callingAPI={callingAPI}
              onDelete={deleteItems}
              onCancel={() => setSelectedRowKeys([])}
            /> : <Filter
              loading={loading}
              params={params}
              environments={environments}
              setParams={(v) => setParams({ ...v, page: 1 })}
            />
          }
        </div>
      }
      {
        total == 0 ? <NoData
          loading={loading}
          className={'mt-6'}
        /> : <>
          {
            isMobile ? <BoxData
              className="mt-4"
              loading={loading}
              data={secrets}
              params={params}
              environments={environments}
              selectedRowKeys={selectedRowKeys}
              onUpdate={handleOpenForm}
              onDelete={deleteItem}
              selectionChange={handleSelectionChange}
            /> : <TableData
              className="mt-4"
              loading={loading}
              data={secrets}
              params={params}
              environments={environments}
              selectedRowKeys={selectedRowKeys}
              onUpdate={handleOpenForm}
              onDelete={deleteItem}
              selectionChange={handleSelectionChange}
            />
          }
        </>
      }
      {
        total > global.constants.PAGE_SIZE && !isMobile && <Pagination
          params={params}
          total={total}
          onChange={handleChangePage}
        />
      }
      <FormData
        visible={formVisible}
        item={selectedItem}
        allItems={secrets}
        environments={environments}
        fetchEnvironments={fetchEnvironments}
        onClose={() => setFormVisible(false)}
        onReload={() => {
          if (!selectedItem) {
            if (params.page === 1) {
              fetchData();
            } else {
              setParams({
                ...params,
                page: 1,
              })
            }
          } else {
            fetchData();
          }
        }}
      />
      <InstallationGuideModal
        visible={installationVisible}
        onClose={() => setInstallationVisible(false)}
      />
    </div>
  );
}

export default ProjectSecrets;